import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@beweegbaas/ui";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Fragment } from "react";
import { useTableNavigation } from "#/hooks/use-table-navigation";
import { getTeacherSportdeelnames } from "#/modules/les.docent-gestuurd/lesson-teacher.functions";
import { baseColorVariants, getGroupColor } from "#/modules/les.shared/group-colors";
import { SportdeelnameFieldCell } from "#/modules/sportdeelname/components/sportdeelname-field-cells";
import type { GroupedSportdeelnameModel, SportdeelnameModel } from "#/schema.nswag";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/teacher/sport-participation",
)({
  loader: ({ params }) => getTeacherSportdeelnames({ data: { lesId: params.lesson } }),
  component: TeacherSportParticipationPage,
});

function TeacherSportParticipationPage() {
  // Enable table navigation for all focusable elements
  useTableNavigation();

  const { locale, schoolyear, school } = Route.useParams();
  const { data } = Route.useLoaderData();
  const { groupedSportdeelnames, sporten, verenigingen } = data;

  return (
    <div className="mx-auto w-full max-w-(--container-max-width)">
      {/* Small size (h-10/px-2 cells) + container-gutter edge cells, inlined from the legacy table variants. */}
      <Table
        aria-label="Sportdeelnames"
        className="[&_td]:h-10 [&_td]:px-2 [&_th]:px-2 [&_tr>*:first-child]:pl-(--container-gutter)! [&_tr>*:last-child]:pr-(--container-gutter)!"
      >
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-10 text-nowrap" />
            <TableHead className="pl-0!">Leerling</TableHead>
            <TableHead className="min-w-[14ch] text-nowrap">
              {m["sportdeelname.sporten"]()}
            </TableHead>
            <TableHead className="min-w-[14ch] text-nowrap">
              {m["sportdeelname.verenigingen"]()}
            </TableHead>
            <TableHead className="min-w-[14ch] text-nowrap">
              {m["sportdeelname.wenssporten"]()}
            </TableHead>
            <TableHead className="min-w-[12ch] text-nowrap">{m["sportdeelname.mrt"]()}</TableHead>
            <TableHead className="min-w-[14ch] text-nowrap">
              {m["sportdeelname.zwemvaardigheid"]()}
            </TableHead>
            <TableHead className="min-w-[14ch] text-nowrap">
              {m["sportdeelname.fietsvaardigheid"]()}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedSportdeelnames.map((group: GroupedSportdeelnameModel, index: number) => (
            <Fragment key={group.groep}>
              {index > 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-10 w-10" colSpan={8} />
                </TableRow>
              )}
              {group.sportdeelnames.map((sd: SportdeelnameModel) => {
                const leerlingData = { sportdeelname: sd, sporten, verenigingen };
                return (
                  <TableRow key={sd.leerlingId}>
                    <TableCell data-table-sticky-left="" className="sticky left-0 z-10 w-10">
                      <span
                        className={cn(
                          "inline-flex size-5 items-center justify-center rounded font-bold",
                          baseColorVariants({
                            color: getGroupColor(group.groep === -1 ? 0 : group.groep),
                            textColor: "dark",
                          }),
                        )}
                      >
                        {group.groep === -1 ? <IconTablerX className="size-4" /> : group.groep}
                      </span>
                    </TableCell>
                    <TableCell
                      data-table-sticky-left="edge"
                      className="sticky left-[calc(var(--container-gutter)+2rem)] z-10 pl-0!"
                    >
                      <Link
                        className="text-nowrap"
                        variant="secondary"
                        underline={true}
                        to="/$locale/{-$schoolyear}/$school/students/$id/dashboard"
                        params={{ locale, schoolyear, school, id: sd.leerlingId }}
                      >
                        {sd.leerlingNaam}
                      </Link>
                      {/* (klasnaam - leerjaar): the backend only fills these when showing them
                          adds information (multi-klas les / combined leerjaren). */}
                      {(sd.klasNaam != null || sd.leerlingLeerjaar != null) && (
                        <span className="text-muted-foreground ml-1">
                          {`(${[sd.klasNaam, sd.leerlingLeerjaar]
                            .filter((part) => part != null)
                            .join(" - ")})`}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <SportdeelnameFieldCell field="sporten" data={leerlingData} />
                    </TableCell>
                    <TableCell>
                      <SportdeelnameFieldCell field="verenigingen" data={leerlingData} />
                    </TableCell>
                    <TableCell>
                      <SportdeelnameFieldCell field="wenssporten" data={leerlingData} />
                    </TableCell>
                    <TableCell>
                      <SportdeelnameFieldCell field="mrt" data={leerlingData} />
                    </TableCell>
                    <TableCell>
                      <SportdeelnameFieldCell field="zwemvaardigheid" data={leerlingData} />
                    </TableCell>
                    <TableCell>
                      <SportdeelnameFieldCell field="fietsvaardigheid" data={leerlingData} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </Fragment>
          ))}
        </TableBody>
      </Table>
      <Outlet />
    </div>
  );
}
